import { describeOpenQuestions } from './clarificationText.mjs';
import clarificationService from '../service/clarificationService.mjs';

const WARNING = {
  title: 'Questions not resolved',
  confirmLabel: 'Approve anyway',
  cancelLabel: 'Go back',
};

/**
 * Approve, but warn first if questions are still open on what is being
 * approved.
 *
 * The server answers an approval with 409 { code: 'OPEN_QUESTIONS',
 * openQuestions } while a clarification question on the claim or report is
 * unresolved (the user chose warn-but-allow, 2026-10-11). This makes the call,
 * and on that answer shows the questions and - if the approver carries on -
 * makes it again with the acknowledgement.
 *
 * `call(acknowledged)` performs the approval. If the approver backs out the
 * returned promise rejects with an error whose `cancelled` is true, which
 * callers treat as "nothing happened" rather than as a failure.
 *
 * @param {Function} confirm  useDialog().confirm
 * @param {Function} call     (acknowledgeOpenQuestions: boolean) => Promise
 */
export const withOpenQuestionsConfirm = async (confirm, call) => {
  try {
    return await call(false);
  } catch (err) {
    if (err?.code !== 'OPEN_QUESTIONS') throw err;
    const ok = await confirm(err.message || 'Some questions are not resolved yet. Approve anyway?', {
      ...WARNING,
      detail: describeOpenQuestions(err.openQuestions || []),
    });
    if (!ok) {
      const cancelled = new Error('Approval cancelled.');
      cancelled.cancelled = true;
      throw cancelled;
    }
    return call(true);
  }
};

export const isCancelled = (err) => !!err?.cancelled;

/**
 * The same warning, asked BEFORE an approval that writes something first -
 * the auditor's report approval saves the claim rows and only then approves,
 * and backing out after the save would leave the rows half-way. Resolves true
 * to go ahead (nothing open, or the approver chose to), false to stop. A
 * failed lookup goes ahead: the warning never blocks an approval.
 *
 * @param {Function} confirm  useDialog().confirm
 * @param {object}   params   { reportId } or { claimType, claimId }
 */
export const confirmOpenQuestionsFirst = async (confirm, params) => {
  let open = [];
  try {
    open = await clarificationService.list({ ...params, status: 'unresolved' });
  } catch {
    return true;
  }
  if (!open.length) return true;
  return confirm(
    `${open.length} question${open.length === 1 ? ' is' : 's are'} not resolved yet. Approve anyway?`,
    { ...WARNING, detail: describeOpenQuestions(open.map(t => ({ ...t, question: t.questionPreview }))) }
  );
};

export default withOpenQuestionsConfirm;
